import type React from 'react';
import ReactECharts from 'echarts-for-react';
import type {MarketIndex} from '../../types/marketTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const COLORS = ['#6B4EFF', '#0F8B8D', '#ED6C02', '#9C27B0', '#2E7D32', '#795548'];
const label = (month: string) => new Date(`${month}T00:00:00Z`).toLocaleDateString(LOCALE, {month: 'short', year: '2-digit', timeZone: 'UTC'});

/** Price paid against its drivers, all indexed to 100 at the first month. Suggested drivers are dotted. */
export const MarketChart: React.FC<{data: MarketIndex}> = ({data}) => {
    const months = [...new Set([...data.priceIndex, ...data.drivers.flatMap(d => d.rebased)].map(p => p.month))].sort();
    const at = (points: {month: string; value: number}[]) => {
        const map = new Map(points.map(p => [p.month, p.value]));
        return months.map(m => map.get(m) ?? null);
    };

    return (
        <ReactECharts style={{height: 320}} notMerge option={{
            grid: {left: 48, right: 24, top: 48, bottom: 32},
            legend: {top: 0, type: 'scroll'},
            tooltip: {trigger: 'axis', valueFormatter: (v: number | null) => (v === null ? '—' : v.toFixed(1))},
            xAxis: {type: 'category', data: months.map(label)},
            yAxis: {type: 'value', scale: true, name: 'Index (start = 100)', nameTextStyle: {align: 'left'}},
            series: [
                {name: 'Price paid', type: 'line', data: at(data.priceIndex), lineStyle: {width: 3, color: '#2D6FF5'},
                    itemStyle: {color: '#2D6FF5'}, symbolSize: 6, connectNulls: true},
                ...(data.composite.length ? [{name: 'Driver mix', type: 'line', data: at(data.composite),
                    lineStyle: {width: 2, type: 'dashed', color: '#1F2229'}, itemStyle: {color: '#1F2229'}, showSymbol: false}] : []),
                ...data.drivers.map((d, i) => ({
                    name: `${d.series.name}${d.status === 'suggested' ? ' (suggested)' : ''}`, type: 'line', data: at(d.rebased),
                    showSymbol: false, connectNulls: true,
                    lineStyle: {width: 1.5, type: d.status === 'suggested' ? 'dotted' : 'solid', color: COLORS[i % COLORS.length]},
                    itemStyle: {color: COLORS[i % COLORS.length]},
                })),
            ],
        }}/>
    );
};
